import { motion } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { copy } from "../data/content";
import { scrollToId } from "../lib/scroll";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-[0.15em] -mb-[0.15em]">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const { t } = useLanguage();

  const scrollToProducts = () => {
    scrollToId("products");
  };

  return (
    <section
      data-testid="hero"
      className="relative min-h-screen w-full overflow-hidden bg-[#F9F9F8]"
    >
      <div className="relative z-10 mx-auto max-w-[1400px] px-6 md:px-12 min-h-screen flex flex-col justify-center pt-24">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex items-center gap-3 mb-8 font-['IBM_Plex_Mono'] text-xs md:text-sm tracking-[0.25em] uppercase text-[#555555]"
        >
          <span className="h-px w-10 bg-[#111111]" />
          {t(copy.hero.kicker)}
        </motion.div>

        <h1 className="font-['Cormorant_Garamond'] font-medium tracking-tight leading-[1.05] text-[#111111] text-6xl sm:text-7xl md:text-8xl lg:text-9xl max-w-5xl">
          <MaskedLine delay={0.35}>{t(copy.hero.line1)}</MaskedLine>
          <MaskedLine delay={0.5}>
            <span className="italic">{t(copy.hero.line2)}</span>
          </MaskedLine>
          <MaskedLine delay={0.65}>{t(copy.hero.line3)}</MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.1, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 max-w-xl font-['IBM_Plex_Mono'] text-sm md:text-base leading-relaxed text-[#555555]"
        >
          {t(copy.hero.intro)}
        </motion.p>

        <motion.button
          data-testid="hero-cta"
          onClick={scrollToProducts}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.3, ease: [0.16, 1, 0.3, 1] }}
          className="group mt-12 inline-flex items-center gap-3 font-['IBM_Plex_Mono'] text-xs tracking-[0.2em] uppercase text-[#111111] focus:outline-none w-fit"
        >
          <span className="relative">
            {t(copy.hero.cta)}
            <span className="absolute left-0 -bottom-1 h-px w-full bg-[#111111] origin-right scale-x-100 transition-transform duration-500 group-hover:origin-left" />
          </span>
          <ArrowDown
            size={16}
            className="transition-transform duration-500 group-hover:translate-y-1"
          />
        </motion.button>
      </div>
    </section>
  );
};
